import React, { useMemo } from 'react';
import useOrganizationUsage from '../../../../../../hooks/api/useOrganizationUsage';
import { AnimateLoadedContent } from '../../../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer, { mainContentPadding } from '../../../../../v2BaseComponents/MainContentContainer';
import Stack, { contentStackSpacing } from '../../../../../v2BaseComponents/Stack';
import { getIntl } from '../../../../../../utils/intl';
import Text from '../../../../../v2BaseComponents/Text';
import SizedView from '../../../../../v2BaseComponents/SizedView';
import {
    VictoryAxis,
    VictoryChart,
    VictoryLine,
    VictoryScatter,
    VictoryTheme,
    VictoryTooltip,
    VictoryVoronoiContainer, // Import VictoryVoronoiContainer
} from 'victory-native';
import getDateString from '../../../../../../utils/getDateString';
import { colors } from '../../../../../../utils/theme';
import MaxWidthRestrictedContent from '../../../../../v2BaseComponents/MaxWidthRestrictedContent';
import { Paper } from '../../../../../v2BaseComponents/Paper';
import { isPresent } from 'ts-is-present';

const intl = getIntl('PaidTab', {
    header: '{value} Current Client{plural}',
    clients: 'Clients',
    client: 'Client',
});

export type PaidTabOwnProps = {};

export type PaidTabProps = PaidTabOwnProps;

const addOrSubMonth = (delta: number, baseDate: Date = new Date()) => {
    const date = new Date(baseDate.getTime());
    date.setMonth(date.getMonth() + delta);
    return date;
};

const MIN_Y_TICKS = 10;
const getYValues = (data: { x: Date; y: number }[]) => {
    const yValues = data.map((d) => d.y);
    const max = Math.max(...yValues);
    const validatedMax = Math.max(max, MIN_Y_TICKS);

    const returnValues: number[] = [];
    const stepValue = Math.ceil(validatedMax / MIN_Y_TICKS);
    for (let i = 0; i < MIN_Y_TICKS; i++) {
        returnValues.push(i * stepValue);
    }

    return returnValues;
};

const MIN_X_TICKS = 3;
const getXValues = (data: { x: Date; y: number }[]) => {
    if (data.length >= MIN_X_TICKS) {
        return data.map((d) => d.x);
    }

    const addBelow = data.length <= 1;
    const sortedData = [...data].sort((a, b) => a.x.getTime() - b.x.getTime());

    return [
        addBelow ? addOrSubMonth(-1, sortedData[0].x) : null,
        ...sortedData.map((_) => _.x),
        addOrSubMonth(1, sortedData[sortedData.length - 1].x),
    ]
        .filter(isPresent)
        .map((d) => new Date(d));
};

const PaidTab: React.FC<PaidTabProps> = () => {
    const { isLoadingUsage, usage } = useOrganizationUsage();
    const data = useMemo(() => {
        const data = (usage?.pastMonthsMaxMedicalProfileCounts || []).map((usage) => ({
            x: new Date(usage.month),
            y: usage.maxMedicalProfileCount,
        }));
        data.push({
            x: new Date(),
            y: usage?.currentMonthMaxMedicalProfileCount || 0,
        });

        return data;
    }, [usage]);

    const getTooltipLabel = (tooltipInput: { datum: { x: Date; y: number } }) => {
        return `${tooltipInput.datum.y} ${intl(tooltipInput.datum.y === 1 ? 'client' : 'clients')}\n${getDateString(
            tooltipInput.datum.x.getTime(),
            'month-year',
        )}`;
    };

    const { xTickValues, yTickValues } = useMemo(
        () => ({
            yTickValues: getYValues(data),
            xTickValues: getXValues(data),
        }),
        [data],
    );

    return (
        <AnimateLoadedContent isLoading={isLoadingUsage}>
            {() => (
                <ActionButtonLayout scrollable whiteBackground>
                    <MaxWidthRestrictedContent>
                        <MainContentContainer>
                            <Stack vertical space={contentStackSpacing} align="center">
                                <Text variant="h4">
                                    {intl('header', {
                                        value: usage?.currentMonthMaxMedicalProfileCount || 0,
                                        plural: usage?.currentMonthMaxMedicalProfileCount === 1 ? '' : 's',
                                    })}
                                </Text>

                                <Paper style={{ width: '100%', padding: mainContentPadding }}>
                                    <SizedView style={{ width: '100%' }}>
                                        {({ width }) => (
                                            <VictoryChart
                                                scale={{
                                                    x: 'time',
                                                }}
                                                width={width}
                                                height={350}
                                                theme={VictoryTheme.material}
                                                containerComponent={<VictoryVoronoiContainer />} // Use VictoryVoronoiContainer
                                            >
                                                <VictoryAxis
                                                    fixLabelOverlap
                                                    tickFormat={(tick) => {
                                                        return getDateString((tick as Date).getTime(), 'month-year');
                                                    }}
                                                    style={{
                                                        grid: { stroke: colors['Primary'], opacity: 0.2 },
                                                        axis: {
                                                            stroke: colors['Dark-Text'],
                                                            strokeWidth: 3,
                                                        },
                                                    }}
                                                    tickValues={xTickValues}
                                                />
                                                <VictoryAxis
                                                    dependentAxis
                                                    style={{
                                                        grid: { stroke: colors['Primary'], opacity: 0.2 },
                                                        axis: {
                                                            stroke: colors['Dark-Text'],
                                                            strokeWidth: 3,
                                                        },
                                                    }}
                                                    tickValues={yTickValues}
                                                />
                                                <VictoryLine
                                                    data={data}
                                                    style={{
                                                        data: {
                                                            stroke: colors['Dark-Text'],
                                                            strokeWidth: 3,
                                                        },
                                                    }}
                                                />
                                                <VictoryScatter
                                                    data={data}
                                                    style={{
                                                        data: {
                                                            fill: colors['Primary-Reg'],
                                                        },
                                                    }}
                                                    size={5}
                                                    labelComponent={<VictoryTooltip />}
                                                    labels={getTooltipLabel}
                                                />
                                            </VictoryChart>
                                        )}
                                    </SizedView>
                                </Paper>
                            </Stack>
                        </MainContentContainer>
                    </MaxWidthRestrictedContent>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(PaidTab);
